import { Mail, MapPin, Phone } from "lucide-react";
import { scrollToId } from "@/lib/smooth";

const WHATSAPP_URL =
  "https://wa.me/393474860922?text=Buongiorno%20Alessio%2C%20vorrei%20informazioni%20su%20Saint%20Anthony";

const WhatsAppIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function Footer() {
  return (
    <>
      <footer data-testid="site-footer" className="bg-stone-950 py-16 text-linen">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 md:grid-cols-3">
          <div>
            <p className="font-serif text-2xl">
              Saint <span className="italic text-oro">Anthony</span>
            </p>
            <p className="mt-3 max-w-xs text-sm font-light leading-relaxed text-linen/60">
              Romantic tiny house nel borgo di Eggi di Spoleto. Giardino privato tra mura secolari
              e piscina SPA gonfiabile riscaldata, aperta tutto l'anno.
            </p>
          </div>
          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-oro">Contatti</p>
            <a href="tel:+393474860922" data-testid="footer-phone" className="flex items-center gap-2 text-sm text-linen/80 transition-colors hover:text-oro">
              <Phone size={14} /> 347 486 0922
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-3 flex items-center gap-2 text-sm text-linen/80 transition-colors hover:text-oro"
            >
              <WhatsAppIcon size={14} /> WhatsApp diretto
            </a>
            <a
              href="mailto:saintanthony.spoleto@hotmail.com"
              data-testid="footer-email"
              className="mt-3 flex items-center gap-2 break-all text-sm text-linen/80 transition-colors hover:text-oro"
            >
              <Mail size={14} className="shrink-0" /> saintanthony.spoleto@hotmail.com
            </a>
            <a
              href="https://maps.app.goo.gl/hTBFZF9JKr9CskCN9"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-address"
              className="mt-3 flex items-start gap-2 text-sm text-linen/80 transition-colors hover:text-oro"
            >
              <MapPin size={14} className="mt-0.5 shrink-0" />
              Via Giovanni Crivellini, 26<br />06049 Eggi, Spoleto (PG)
            </a>
          </div>
          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-oro">Esplora</p>
            <div className="flex flex-col gap-2 text-sm text-linen/80">
              {[
                ["dimora", "La Casa"],
                ["spazi", "Gli Spazi"],
                ["galleria", "Galleria"],
                ["spa", "Giardino & SPA"],
                ["disponibilita", "Disponibilità"],
                ["prenota", "Prenota"],
              ].map(([id, label]) => (
                <button key={id} data-testid={`footer-link-${id}`} onClick={() => scrollToId(id)} className="w-fit text-left transition-colors hover:text-oro">
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mx-auto mt-14 flex max-w-7xl flex-wrap items-center justify-between gap-4 border-t border-linen/10 px-5 pt-8 sm:px-8">
          <p className="text-xs text-linen/40">© {new Date().getFullYear()} Saint Anthony — Eggi di Spoleto. Foto panoramiche: Wikimedia Commons (CC BY-SA).</p>
          <a href="/gestione" data-testid="footer-admin-link" className="text-xs text-linen/30 transition-colors hover:text-oro">
            Area riservata
          </a>
        </div>
      </footer>

      <a
        data-testid="whatsapp-float"
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Scrivici su WhatsApp"
        className="fixed bottom-5 right-5 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl shadow-stone-900/30 transition-transform duration-300 hover:scale-110 active:scale-95"
      >
        <WhatsAppIcon size={26} />
      </a>
    </>
  );
}
